import { useEffect, useState, useCallback, useRef } from 'react';
import { getContentEmbedUIOrigin, ContentEmbedUIEvents, CONTENT_EMBED_UI_BASE_URL, logDebug } from 'rich-text-lib/constants/embed';
// The following is adapted from rich-text-lib's EmbedLivePreview & EmbedIframe

// Html passed to this component is rendered on a separate page (non-hubspot domain) using content-embed-ui
// then that page's url is used as the src for the iframe that this component renders
// Rendering the content on a separate page allows us to more securely allow any script in an embed code to run
// enviro.setDebug('CONTENT_EMBED_UI') to follow communications with content-embed-ui
const ContentEmbedUIOrigin = getContentEmbedUIOrigin();
export const useEmbedIframeReport = ({
  reportId,
  html,
  onSetContentDone
}) => {
  const IFRAME_ID = `dashboard-embed-${reportId || 'preview'}`;
  const [targetWindow, setTargetWindow] = useState(null);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const contentRef = useRef(html);
  const postMessageToIframe = useCallback(msg => {
    if (!targetWindow) return;
    logDebug('Sending postMessage to content-embed-ui iframe:', msg, ContentEmbedUIOrigin);
    targetWindow.postMessage(Object.assign({}, msg, {
      id: IFRAME_ID
    }), ContentEmbedUIOrigin);
  }, [targetWindow, IFRAME_ID]);
  useEffect(() => {
    if (!html) return;
    contentRef.current = html;

    // Asynchronously set the iframe content if `html` prop changed after
    // the connection with the Iframe was already made
    postMessageToIframe({
      action: ContentEmbedUIEvents.SET_CONTENT,
      payload: {
        html
      }
    });
  }, [postMessageToIframe, html]);
  useEffect(() => {
    const onPostMessage = evt => {
      const {
        origin,
        source,
        data
      } = evt;
      if (!targetWindow || origin !== ContentEmbedUIOrigin || source !== targetWindow) {
        return;
      }

      // Interframe messages are encapsulated in the { body } property
      const {
        action,
        id
      } = data.body || {};
      if (id !== IFRAME_ID) return;
      logDebug('Received postMessage from content-embed-ui iframe:', data, origin);
      if (action === ContentEmbedUIEvents.READY && contentRef.current) {
        postMessageToIframe({
          action: ContentEmbedUIEvents.SET_CONTENT,
          payload: {
            html: contentRef.current
          }
        });
      } else if (action === ContentEmbedUIEvents.SET_CONTENT_DONE) {
        setIsContentLoaded(true);
        onSetContentDone();
      }
    };
    window.addEventListener('message', onPostMessage);
    return () => {
      // Clean up the content-embed-ui app
      postMessageToIframe({
        action: ContentEmbedUIEvents.DISCONNECT
      });
      window.removeEventListener('message', onPostMessage);
    };
  }, [targetWindow, postMessageToIframe, onSetContentDone, IFRAME_ID]);
  const getSource = () => {
    const query = encodeURIComponent(`id=${IFRAME_ID}&inpage=false&origin=${window.location.origin}`);
    return `${CONTENT_EMBED_UI_BASE_URL}?${query}`;
  };
  const getRefFromElement = el => {
    if (el && el.contentWindow) {
      setTargetWindow(el.contentWindow);
    }
  };
  return {
    iframeWidgetKey: `dashboard-iframe-${IFRAME_ID}`,
    src: getSource(),
    isContentLoaded,
    getRefFromElement
  };
};